/**
 * AdminSettingsPage — owner mengatur isi landing tanpa perlu deploy ulang.
 *
 * Jumlah produk sengaja TIDAK bisa diisi manual: angkanya dihitung langsung
 * dari katalog di server, supaya tidak pernah berbeda dari kenyataan.
 */

import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Panel } from "@/components/ui/Panel";
import { Button } from "@/components/ui/Button";
import { Field, Input, Textarea } from "@/components/ui/Form";
import { MonoText } from "@/components/ui/CopyButton";
import { LoadingState, ErrorState } from "@/components/ui/States";
import { invalidateLanding } from "@/hooks/useLanding";
import { formatIDR } from "@/lib/format";
import { strikethroughPrice, savingsPercent } from "@ag5r-store/shared";
import type { SiteSettings } from "@ag5r-store/shared";

const EMPTY: SiteSettings = {
  hero_eyebrow: "",
  hero_title: "",
  hero_subtitle: "",
  hero_image_url: "",
  cta_primary_label: "",
  cta_primary_href: "",
  cta_secondary_label: "",
  cta_secondary_href: "",
  stat_clients: "",
  stat_rating: "",
  footer_tagline: "",
  discount_percent: 0,
};

export function AdminSettingsPage() {
  const { user, loading: authLoading } = useAuth();
  const [form, setForm] = useState<SiteSettings>(EMPTY);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [uploading, setUploading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setForm(await api.getSettingsAdmin());
    } catch (e) {
      setError(e instanceof Error ? e.message : "Gagal memuat settings");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!authLoading && user?.role === "owner") load();
  }, [user, authLoading, load]);

  function set<K extends keyof SiteSettings>(key: K, value: SiteSettings[K]) {
    setForm((f) => ({ ...f, [key]: value }));
    setSaved(false);
  }

  async function handleHeroUpload(files: FileList | null) {
    const file = files?.[0];
    if (!file) return;
    setUploading(true);
    setError(null);
    try {
      const sig = await api.getCloudinarySign();
      const body = new FormData();
      body.append("file", file);
      body.append("api_key", sig.api_key);
      body.append("timestamp", String(sig.timestamp));
      body.append("signature", sig.signature);
      body.append("folder", sig.folder);

      const res = await fetch(
        `https://api.cloudinary.com/v1_1/${sig.cloud_name}/image/upload`,
        { method: "POST", body },
      );
      if (!res.ok) {
        let detail = `HTTP ${res.status}`;
        try {
          const j = (await res.json()) as { error?: { message?: string } };
          if (j?.error?.message) detail = j.error.message;
        } catch {
          // Body bukan JSON — status code sudah cukup informatif.
        }
        throw new Error(`Cloudinary menolak gambar: ${detail}`);
      }
      const json = (await res.json()) as { secure_url: string };
      set("hero_image_url", json.secure_url);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Upload gagal");
    } finally {
      setUploading(false);
    }
  }

  async function handleSave() {
    setSaving(true);
    setError(null);
    try {
      await api.updateSettings(form);
      // Buang cache supaya landing & footer langsung memakai teks baru.
      invalidateLanding();
      setSaved(true);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Gagal menyimpan");
    } finally {
      setSaving(false);
    }
  }

  if (authLoading || loading) return <LoadingState label="LOADING_SETTINGS" />;
  if (!user || user.role !== "owner") {
    return (
      <div className="max-w-xl mx-auto px-4 py-20 text-center">
        <MonoText size="sm" color="status-failed">[ ACCESS_DENIED ]</MonoText>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-4 py-10">
      <div className="mb-8">
        <h1 className="font-heading text-3xl font-extrabold tracking-tight text-ink-primary">
          Pengaturan Landing
        </h1>
        <p className="mt-2 font-sans text-sm text-ink-muted">
          Ubah teks, gambar, dan tombol di halaman depan. Perubahan langsung terlihat setelah disimpan.
        </p>
      </div>

      {error && <div className="mb-6"><ErrorState error={error} onRetry={load} /></div>}

      <div className="space-y-5">
        <Panel title="HERO">
          <div className="space-y-4">
            <Field label="LABEL KECIL DI ATAS JUDUL" hint="Kosongkan kalau tidak perlu.">
              <Input
                value={form.hero_eyebrow}
                onChange={(e) => set("hero_eyebrow", e.target.value)}
                placeholder="Toko Lisensi Digital"
              />
            </Field>
            <Field label="JUDUL UTAMA" required>
              <Input
                value={form.hero_title}
                onChange={(e) => set("hero_title", e.target.value)}
                placeholder="Beli sekarang, aktif detik ini juga."
              />
            </Field>
            <Field label="PENJELASAN" hint="Satu sampai dua kalimat. Fokus ke manfaat, bukan teknologi.">
              <Textarea
                value={form.hero_subtitle}
                onChange={(e) => set("hero_subtitle", e.target.value)}
                rows={3}
              />
            </Field>
          </div>
        </Panel>

        <Panel title="GAMBAR HERO">
          <div className="space-y-4">
            <Field label="URL GAMBAR" hint="Kosongkan kalau tidak mau menampilkan gambar.">
              <Input
                value={form.hero_image_url}
                onChange={(e) => set("hero_image_url", e.target.value)}
                placeholder="https://res.cloudinary.com/..."
              />
            </Field>
            <div>
              <label className="font-mono text-2xs uppercase tracking-widest text-ink-muted block mb-1.5">
                ATAU UPLOAD
              </label>
              <input
                type="file"
                accept="image/*"
                disabled={uploading}
                onChange={(e) => void handleHeroUpload(e.target.files)}
                className="block w-full text-sm text-ink-muted file:mr-3 file:border file:border-border
                           file:bg-elevated file:px-3 file:py-1.5 file:text-ink-primary file:font-mono
                           file:text-xs hover:file:border-accent"
              />
              {uploading && (
                <MonoText size="xs" color="ink-muted" className="mt-2 block">
                  Mengunggah…
                </MonoText>
              )}
            </div>
            {form.hero_image_url && (
              <div className="border border-border bg-panel overflow-hidden max-w-sm">
                {/* Rasio sama dengan landing, supaya pratinjau di sini
                    memperlihatkan potongan yang benar-benar akan tampil. */}
                <img src={form.hero_image_url} alt="" className="w-full aspect-square object-cover" />
              </div>
            )}
          </div>
        </Panel>

        <Panel title="TOMBOL">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="TOMBOL UTAMA — TEKS">
              <Input
                value={form.cta_primary_label}
                onChange={(e) => set("cta_primary_label", e.target.value)}
                placeholder="Lihat Katalog"
              />
            </Field>
            <Field label="TOMBOL UTAMA — TUJUAN" hint="Contoh: /products">
              <Input
                value={form.cta_primary_href}
                onChange={(e) => set("cta_primary_href", e.target.value)}
                placeholder="/products"
              />
            </Field>
            <Field label="TOMBOL KEDUA — TEKS" hint="Kosongkan untuk menyembunyikan.">
              <Input
                value={form.cta_secondary_label}
                onChange={(e) => set("cta_secondary_label", e.target.value)}
                placeholder="Cara Kerja"
              />
            </Field>
            <Field label="TOMBOL KEDUA — TUJUAN" hint="Contoh: #cara-kerja">
              <Input
                value={form.cta_secondary_href}
                onChange={(e) => set("cta_secondary_href", e.target.value)}
                placeholder="#cara-kerja"
              />
            </Field>
          </div>
        </Panel>

        <Panel title="ANGKA DI HERO">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="JUMLAH PEMBELI" hint="Teks bebas, contoh: 300+">
              <Input
                value={form.stat_clients}
                onChange={(e) => set("stat_clients", e.target.value)}
                placeholder="300+"
              />
            </Field>
            <Field label="RATING" hint="Contoh: 4.8">
              <Input
                value={form.stat_rating}
                onChange={(e) => set("stat_rating", e.target.value)}
                placeholder="4.8"
              />
            </Field>
          </div>
          <MonoText size="xs" color="ink-dim" className="mt-3 block">
            Jumlah produk tidak bisa diisi di sini — dihitung otomatis dari katalog aktif.
          </MonoText>
        </Panel>

        <Panel title="HARGA CORET">
          <Field
            label="PERSEN DI ATAS HARGA JUAL"
            hint="0 = matikan fitur ini. Maksimal 90."
          >
            <Input
              type="number"
              min="0"
              max="90"
              value={String(form.discount_percent)}
              onChange={(e) => set("discount_percent", Number(e.target.value) || 0)}
              placeholder="40"
            />
          </Field>

          {form.discount_percent > 0 ? (
            <div className="mt-3 border border-border bg-panel rounded-sm px-3 py-2.5">
              <MonoText size="xs" color="ink-muted" className="block mb-1">
                CONTOH TAMPILAN
              </MonoText>
              <p className="font-sans text-sm">
                <span className="text-ink-dim line-through mr-2">
                  {formatIDR(strikethroughPrice(50000, form.discount_percent) ?? 0)}
                </span>
                <span className="text-accent font-mono">{formatIDR(50000)}</span>
                <span className="ml-2 text-xs text-status-active">
                  hemat{" "}
                  {savingsPercent(50000, strikethroughPrice(50000, form.discount_percent) ?? 0)}%
                </span>
              </p>
              <p className="mt-2 font-sans text-xs text-ink-muted">
                Pembeli tetap membayar harga varian. Angka yang dicoret hanya tampilan
                dan tidak memengaruhi tagihan.
              </p>
            </div>
          ) : (
            <MonoText size="xs" color="ink-dim" className="mt-3 block">
              Harga coret mati — katalog hanya menampilkan harga varian apa adanya.
            </MonoText>
          )}

          <p className="mt-3 font-sans text-xs text-status-pending">
            Perlu diketahui: harga coret yang tidak pernah benar-benar berlaku dapat
            dianggap klaim menyesatkan menurut UU Perlindungan Konsumen 8/1999.
          </p>
        </Panel>

        <Panel title="FOOTER">
          <Field label="TAGLINE FOOTER">
            <Input
              value={form.footer_tagline}
              onChange={(e) => set("footer_tagline", e.target.value)}
              placeholder="Lisensi digital yang langsung aktif setelah bayar."
            />
          </Field>
        </Panel>
      </div>

      <div className="mt-8 flex items-center gap-3">
        <Button variant="primary" onClick={handleSave} loading={saving}>
          SIMPAN PERUBAHAN
        </Button>
        {saved && <MonoText size="xs" color="cyan">Tersimpan.</MonoText>}
      </div>
    </div>
  );
}
